import * as Sentry from "@sentry/nextjs"; // Add this import
import { NextRouter, withRouter } from "next/router";
import React from "react";
import { trackAmplitudeEvent } from "../../../utils/amplitude";
import { alert } from "../../../utils/APICalls/APICalls";
import { PagesEnum } from "../../../utils/enums";
import { AlertErrorType } from "../../../utils/ErrorTypes";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import { SadFaceIcon } from "../../global/Icons/Icons";
import styles from "./ErrorBoundary.module.scss";

const util = require("util");

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  { router: NextRouter; children?: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: any) {
    super(props);

    this.state = { hasError: false };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  async componentDidCatch(error: any, errorInfo: any) {
    console.log(
      "error boundary caught this error: ",
      util.inspect(
        {
          error,
          errorInfo,
        },
        { depth: null },
      ),
    );
    const currentUrl = window?.location?.href;

    Sentry.withScope((scope) => {
      scope.setExtra("componentStack", errorInfo.componentStack);
      scope.setExtra("currentUrl", currentUrl);
      Sentry.captureException(error);
    });

    await alert(`${AlertErrorType.ClientSide_Exception}`, {
      error: util.inspect(error, { depth: null }),
      errorInfo,
      currentUrl,
    });

    // Amplitude error tracking
    try {
      trackAmplitudeEvent("Client Side Error", {
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        componentStack: errorInfo.componentStack,
        currentUrl,
        errorType: AlertErrorType.ClientSide_Exception,
        timestamp: new Date().toISOString(),
      });
    } catch (amplitudeError) {
      console.log("Amplitude error tracking failed:", amplitudeError);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={`${styles.container} bg__gradient`}>
          <div className={styles.svg_container}>
            <SadFaceIcon />
          </div>
          <div className={styles.title}>Oops!</div>
          <div className={styles.info}>Something went wrong</div>
          <div className={styles.padded_container}>
            <button
              className={styles.btn}
              data-testid={`generic-error-modal-button`}
              onClick={() =>
                window.location.replace(
                  getPageLink(this.props.router, PagesEnum.Home).as,
                )
              }
              style={{ fontSize: "1rem" }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withRouter(ErrorBoundary);
